import { useState, useEffect, Fragment, useMemo, useId } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2, Users, Search, X, Settings } from 'lucide-react'
import { GRADES } from '@/lib/constants'
import { useAuth } from '@/hooks/useAuth'
import { useFeedback } from '@/components/ui/feedback'
import {
  getSubmissions,
  getResults,
  getVotes,
  updatePhase,
  publishPhotos,
  approveSubmission,
  rejectSubmission,
  deleteSubmission,
  isFinalized,
  type Submission,
  type Competition,
  type VoteResult,
  type IndividualVote,
} from '@/lib/admin-api'

const PHASES = [
  { value: 'setup', label: 'Setup' },
  { value: 'submission', label: 'Submission' },
  { value: 'moderation', label: 'Moderation' },
  { value: 'voting', label: 'Voting' },
  { value: 'closed', label: 'Closed' },
]

type TopTab = 'submissions' | 'results' | 'settings'
type SubmissionTab = 'draft' | 'pending' | 'approved' | 'rejected'

export default function AdminDashboardPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading, signOut, getToken, email } = useAuth()
  const { toast, confirm } = useFeedback()

  const [competition, setCompetition] = useState<Competition | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [results, setResults] = useState<VoteResult[]>([])
  const [votes, setVotes] = useState<IndividualVote[]>([])
  const [activeTopTab, setActiveTopTab] = useState<TopTab>('submissions')
  const [activeSubmissionTab, setActiveSubmissionTab] = useState<SubmissionTab>('pending')
  const [searchQuery, setSearchQuery] = useState('')
  const [gradeFilter, setGradeFilter] = useState<string>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<{ photoUrl?: string; photoTitle: string; name: string } | null>(
    null
  )

  // Counts
  const draftCount = submissions.filter((s) => s.status === 'draft').length
  const pendingCount = submissions.filter((s) => s.status === 'pending').length
  const approvedCount = submissions.filter((s) => s.status === 'approved').length
  const rejectedCount = submissions.filter((s) => s.status === 'rejected').length

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login')
    }
  }, [authLoading, isAuthenticated, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when auth flips true; loadData is re-created each render and isn't a meaningful dep
  }, [isAuthenticated])

  // Set default tab based on phase and submission counts
  useEffect(() => {
    if (competition?.phase) {
      if (competition.phase === 'voting' || competition.phase === 'closed') {
        setActiveTopTab('results')
      } else {
        setActiveTopTab('submissions')
      }
    }
  }, [competition?.phase])

  // Set default submission tab based on counts
  useEffect(() => {
    if (pendingCount > 0) {
      setActiveSubmissionTab('pending')
    } else if (approvedCount > 0) {
      setActiveSubmissionTab('approved')
    } else {
      setActiveSubmissionTab('pending')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-pick the default tab when the submission set loads/changes; the counts are derived from `submissions` and would re-run mid-interaction
  }, [submissions.length])

  // Close photo modal on Escape
  useEffect(() => {
    if (!selectedPhoto) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedPhoto(null)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedPhoto])

  const loadData = async () => {
    const token = await getToken()
    if (!token) return

    setIsLoading(true)

    // Load all data in parallel
    const [submissionsResult, resultsResult, votesResult] = await Promise.all([
      getSubmissions(token),
      getResults(token),
      getVotes(token),
    ])

    if (submissionsResult.error) {
      setError(submissionsResult.error)
    } else if (submissionsResult.data) {
      setCompetition(submissionsResult.data.competition)
      setSubmissions(submissionsResult.data.submissions)
    }

    if (resultsResult.data) {
      const sorted = [...resultsResult.data.results].sort((a, b) => b.totalPoints - a.totalPoints)
      setResults(sorted)
    }

    if (votesResult.data) {
      setVotes(votesResult.data.votes)
    }

    setIsLoading(false)
  }

  const handlePhaseChange = async (phase: string) => {
    const token = await getToken()
    if (!token) return

    setIsUpdating(true)
    const { error } = await updatePhase(token, phase)

    if (error) {
      setError(error)
    } else {
      setCompetition((prev) => (prev ? { ...prev, phase } : null))
    }
    setIsUpdating(false)
  }

  const handlePublish = async () => {
    const token = await getToken()
    if (!token) return

    const confirmed = await confirm({
      title: 'Publish photos',
      message: 'This will publish all approved photos for voting. Continue?',
      confirmLabel: 'Publish',
    })
    if (!confirmed) return

    setIsUpdating(true)
    const { data, error } = await publishPhotos(token)

    if (error) {
      setError(error)
    } else {
      setCompetition((prev) => (prev ? { ...prev, phase: 'voting' } : null))
      toast(`Published ${data?.count} photos for voting!`, { variant: 'success' })
    }
    setIsUpdating(false)
  }

  const handleApprove = async (submissionId: string) => {
    const token = await getToken()
    if (!token) return

    setActionLoading(submissionId)
    const { error } = await approveSubmission(token, submissionId)

    if (error) {
      setError(error)
    } else {
      // Only finalized submissions can be approved, so cast is safe
      setSubmissions((prev) =>
        prev.map((s) => (s.id === submissionId && isFinalized(s) ? { ...s, status: 'approved' as const } : s))
      )
    }
    setActionLoading(null)
  }

  const handleReject = async (submissionId: string) => {
    const reason = await confirm({
      title: 'Reject submission',
      confirmLabel: 'Reject',
      destructive: true,
      input: { label: 'Rejection reason (optional)' },
    })
    if (reason === null) return

    const token = await getToken()
    if (!token) return

    setActionLoading(submissionId)
    const { error } = await rejectSubmission(token, submissionId, reason || undefined)

    if (error) {
      setError(error)
    } else {
      // Only finalized submissions can be rejected, so cast is safe
      setSubmissions((prev) =>
        prev.map((s) =>
          s.id === submissionId && isFinalized(s)
            ? { ...s, status: 'rejected' as const, rejectionReason: reason || undefined }
            : s
        )
      )
    }
    setActionLoading(null)
  }

  const handleDelete = async (submissionId: string) => {
    const confirmed = await confirm({
      title: 'Delete submission',
      message: 'Delete this submission? The student will be able to submit again.',
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!confirmed) return

    const token = await getToken()
    if (!token) return

    setActionLoading(submissionId)
    const { error } = await deleteSubmission(token, submissionId)

    if (error) {
      setError(error)
    } else {
      setSubmissions((prev) => prev.filter((s) => s.id !== submissionId))
    }
    setActionLoading(null)
  }

  const formatGrade = (grade: string | undefined) => {
    if (!grade) return 'Unknown'
    // If already in correct format, return as-is
    if (grade.startsWith('Class of 20')) return grade
    // Handle "class_of_2026" or "class_of_26" formats
    let normalized = grade
      .replace(/^class_of_/i, '')
      .replace(/_/g, ' ')
      .trim()
    // If it's just a 2-digit year, prepend "20"
    if (/^\d{2}$/.test(normalized)) {
      normalized = '20' + normalized
    }
    return `Class of ${normalized}`
  }

  // Filter submissions by status, search query, and grade
  const filteredSubmissions = useMemo(() => {
    return submissions.filter((s) => {
      // Filter by status
      if (s.status !== activeSubmissionTab) return false

      // Filter by grade (drafts may not have grade set)
      if (gradeFilter !== 'all') {
        if (!s.grade || formatGrade(s.grade) !== gradeFilter) return false
      }

      // Filter by search query (name or email)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        const matchesName = s.name?.toLowerCase().includes(query)
        const matchesEmail = s.email?.toLowerCase().includes(query)
        if (!matchesName && !matchesEmail) return false
      }

      return true
    })
  }, [submissions, activeSubmissionTab, searchQuery, gradeFilter])

  if (authLoading || isLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-foreground-muted">Loading...</p>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen">
      {/* Header */}
      <header className="bg-surface border-border border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <h1 className="text-foreground font-display text-xl font-bold">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            {/* Phase Selector */}
            <select
              value={competition?.phase || ''}
              onChange={(e) => handlePhaseChange(e.target.value)}
              disabled={isUpdating}
              className="border-border bg-surface text-foreground focus:ring-accent rounded-lg border px-3 py-1.5 text-sm focus:ring-2 focus:outline-none"
            >
              {PHASES.map((phase) => (
                <option key={phase.value} value={phase.value}>
                  {phase.label}
                </option>
              ))}
            </select>

            <span className="text-foreground-muted hidden text-sm sm:block">{email}</span>
            <button onClick={signOut} className="text-foreground-muted hover:text-foreground text-sm transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {error && (
          <div className="bg-error/10 border-error/20 text-error mb-4 flex items-center justify-between rounded-lg border p-4 text-sm">
            {error}
            <button onClick={() => setError(null)} className="underline">
              Dismiss
            </button>
          </div>
        )}

        {/* Publish Button (moderation phase only) */}
        {competition?.phase === 'moderation' && approvedCount > 0 && (
          <div className="mb-4">
            <button
              onClick={handlePublish}
              disabled={isUpdating}
              className="bg-success text-success-foreground hover:bg-success/90 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              Publish {approvedCount} Photos for Voting
            </button>
          </div>
        )}

        {/* Top-Level Tabs */}
        <div className="border-border mb-4 flex gap-2 border-b pb-4">
          <button
            onClick={() => setActiveTopTab('submissions')}
            className={`rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors ${
              activeTopTab === 'submissions'
                ? 'bg-foreground text-background'
                : 'bg-surface text-foreground-muted border-border hover:bg-surface-hover border'
            }`}
          >
            Submissions ({submissions.length})
          </button>
          <button
            onClick={() => setActiveTopTab('results')}
            className={`rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors ${
              activeTopTab === 'results'
                ? 'bg-foreground text-background'
                : 'bg-surface text-foreground-muted border-border hover:bg-surface-hover border'
            }`}
          >
            Voting Results ({votes.length} voters)
          </button>
          <button
            onClick={() => setActiveTopTab('settings')}
            className={`rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors ${
              activeTopTab === 'settings'
                ? 'bg-foreground text-background'
                : 'bg-surface text-foreground-muted border-border hover:bg-surface-hover border'
            }`}
          >
            <Settings className="mr-1.5 inline h-4 w-4" />
            Settings
          </button>
        </div>

        {/* Submission Sub-Tabs (only show when on submissions) */}
        {activeTopTab === 'submissions' && (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              {draftCount > 0 && (
                <button
                  onClick={() => setActiveSubmissionTab('draft')}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    activeSubmissionTab === 'draft'
                      ? 'bg-foreground-muted text-background'
                      : 'bg-surface text-foreground-muted border-border hover:bg-surface-hover border'
                  }`}
                >
                  Drafts ({draftCount})
                </button>
              )}
              <button
                onClick={() => setActiveSubmissionTab('pending')}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeSubmissionTab === 'pending'
                    ? 'bg-secondary text-secondary-foreground'
                    : 'bg-surface text-foreground-muted border-border hover:bg-surface-hover border'
                }`}
              >
                Pending ({pendingCount})
              </button>
              <button
                onClick={() => setActiveSubmissionTab('approved')}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeSubmissionTab === 'approved'
                    ? 'bg-success text-success-foreground'
                    : 'bg-surface text-foreground-muted border-border hover:bg-surface-hover border'
                }`}
              >
                Approved ({approvedCount})
              </button>
              {rejectedCount > 0 && (
                <button
                  onClick={() => setActiveSubmissionTab('rejected')}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    activeSubmissionTab === 'rejected'
                      ? 'bg-error text-error-foreground'
                      : 'bg-surface text-foreground-muted border-border hover:bg-surface-hover border'
                  }`}
                >
                  Rejected ({rejectedCount})
                </button>
              )}
            </div>

            {/* Search and Filters */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row">
              {/* Search Input */}
              <div className="relative max-w-md flex-1">
                <Search className="text-foreground-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-border bg-surface text-foreground placeholder:text-foreground-muted focus:ring-accent w-full rounded-lg border py-2 pr-10 pl-10 text-sm focus:ring-2 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-foreground-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Grade Filter */}
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="border-border bg-surface text-foreground focus:ring-accent rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
              >
                <option value="all">All Classes</option>
                {GRADES.map((grade) => (
                  <option key={grade.value} value={grade.value}>
                    {grade.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Tab Content */}
        {activeTopTab === 'results' ? (
          <ResultsView results={results} votes={votes} formatGrade={formatGrade} onPhotoClick={setSelectedPhoto} />
        ) : activeTopTab === 'settings' ? (
          <SettingsView />
        ) : (
          <SubmissionsGrid
            submissions={filteredSubmissions}
            activeTab={activeSubmissionTab}
            actionLoading={actionLoading}
            onApprove={handleApprove}
            onReject={handleReject}
            onDelete={handleDelete}
            onPhotoClick={setSelectedPhoto}
            formatGrade={formatGrade}
            hasFilters={!!searchQuery.trim() || gradeFilter !== 'all'}
          />
        )}
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop-dismiss; keyboard users close via the Escape handler above
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="max-h-[90vh] max-w-4xl">
            <img
              src={selectedPhoto.photoUrl}
              alt={selectedPhoto.photoTitle}
              className="max-h-[90vh] max-w-full object-contain"
            />
            <p className="mt-2 text-center text-white">
              {selectedPhoto.photoTitle} by {selectedPhoto.name}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// Submissions Grid Component
interface SubmissionsGridProps {
  submissions: Submission[]
  activeTab: SubmissionTab
  actionLoading: string | null
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onDelete: (id: string) => void
  onPhotoClick: (photo: { photoUrl?: string; photoTitle: string; name: string }) => void
  formatGrade: (grade: string | undefined) => string
  hasFilters?: boolean
}

function SubmissionsGrid({
  submissions,
  activeTab,
  actionLoading,
  onApprove,
  onReject,
  onDelete,
  onPhotoClick,
  formatGrade,
  hasFilters = false,
}: SubmissionsGridProps) {
  if (submissions.length === 0) {
    return (
      <div className="text-foreground-muted py-12 text-center">
        {hasFilters ? 'No submissions match your search.' : `No ${activeTab} submissions.`}
      </div>
    )
  }

  // Pending: 1 column on mobile for easier review actions
  // Approved/Rejected: 2 columns on mobile for quick overview
  const gridClass =
    activeTab === 'pending'
      ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'
      : 'grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-3'

  const isCompact = activeTab !== 'pending'

  return (
    <div className={gridClass}>
      {submissions.map((submission) => (
        <div key={submission.id} className="border-border bg-surface overflow-hidden rounded-xl border">
          {/* Photo */}
          <div
            role="button"
            tabIndex={0}
            className={`bg-surface-hover group relative aspect-square ${submission.photoUrl ? 'cursor-pointer' : ''}`}
            onClick={() =>
              submission.photoUrl &&
              onPhotoClick({
                photoUrl: submission.photoUrl,
                photoTitle: submission.photoTitle || 'Untitled',
                name: submission.name || 'Unknown',
              })
            }
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && submission.photoUrl) {
                e.preventDefault()
                onPhotoClick({
                  photoUrl: submission.photoUrl,
                  photoTitle: submission.photoTitle || 'Untitled',
                  name: submission.name || 'Unknown',
                })
              }
            }}
          >
            {submission.photoUrl ? (
              <img
                src={submission.photoUrl}
                alt={submission.photoTitle || 'Submission photo'}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="text-foreground-muted flex h-full w-full items-center justify-center">
                <span className="text-sm">No photo yet</span>
              </div>
            )}
            {/* Delete button overlay */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete(submission.id)
              }}
              disabled={actionLoading === submission.id}
              className="hover:bg-error absolute top-2 right-2 rounded-lg bg-black/50 p-2 text-white opacity-0 transition-all group-hover:opacity-100 disabled:opacity-50"
              title="Delete submission"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          {/* Info - compact for 2-column view */}
          <div className={isCompact ? 'p-2 sm:p-4' : 'p-4'}>
            <h3 className={`text-foreground truncate font-semibold ${isCompact ? 'text-sm' : ''}`}>
              {submission.photoTitle || <span className="text-foreground-muted italic">Untitled</span>}
            </h3>
            <p className={`text-foreground-muted truncate ${isCompact ? 'text-xs' : 'text-sm'}`}>
              {submission.name || <span className="italic">No name</span>}
            </p>
            <p className={`text-foreground-subtle truncate ${isCompact ? 'text-xs' : 'text-xs'}`}>
              {formatGrade(submission.grade)}
            </p>
            {!isCompact && <p className="text-foreground-subtle mt-1 text-xs">{submission.email}</p>}

            {/* Actions for pending */}
            {activeTab === 'pending' && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => onApprove(submission.id)}
                  disabled={actionLoading === submission.id}
                  className="bg-success text-success-foreground hover:bg-success/90 flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  onClick={() => onReject(submission.id)}
                  disabled={actionLoading === submission.id}
                  className="bg-error text-error-foreground hover:bg-error/90 flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            )}

            {/* Info for drafts */}
            {activeTab === 'draft' && (
              <p className="text-foreground-muted mt-2 text-xs italic">In progress - student hasn't submitted yet</p>
            )}

            {isFinalized(submission) && submission.rejectionReason && (
              <p className="text-error mt-2 text-xs">Reason: {submission.rejectionReason}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// Results View Component
interface ResultsViewProps {
  results: VoteResult[]
  votes: IndividualVote[]
  formatGrade: (grade: string) => string
  onPhotoClick: (photo: { photoUrl?: string; photoTitle: string; name: string }) => void
}

function ResultsView({ results, votes, formatGrade, onPhotoClick }: ResultsViewProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [gradeFilter, setGradeFilter] = useState<string>('all')
  const gradeFilterId = useId()

  // Filter results by grade
  const filteredResults = useMemo(() => {
    if (gradeFilter === 'all') return results
    return results.filter((r) => r.submission?.grade === gradeFilter)
  }, [results, gradeFilter])

  // Group votes by submission ID
  const getVotersForSubmission = (submissionId: string) => {
    const firstVoters = votes.filter((v) => v.firstChoice.id === submissionId).map((v) => v.email)
    const secondVoters = votes.filter((v) => v.secondChoice.id === submissionId).map((v) => v.email)
    const thirdVoters = votes.filter((v) => v.thirdChoice.id === submissionId).map((v) => v.email)
    return { firstVoters, secondVoters, thirdVoters }
  }

  if (results.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-foreground-muted">No voting results yet.</p>
        <p className="text-foreground-subtle mt-1 text-sm">Results will appear after students have voted.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Grade Filter */}
      <div className="flex items-center gap-3">
        <label htmlFor={gradeFilterId} className="text-foreground-muted text-sm">
          Filter by class:
        </label>
        <select
          id={gradeFilterId}
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value)}
          className="border-border bg-surface text-foreground focus:ring-accent rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
        >
          <option value="all">All Classes</option>
          {GRADES.map((grade) => (
            <option key={grade.value} value={grade.value}>
              {grade.label}
            </option>
          ))}
        </select>
        {gradeFilter !== 'all' && (
          <span className="text-foreground-muted text-sm">
            Showing {filteredResults.length} of {results.length} results
          </span>
        )}
      </div>

      <div className="border-border bg-surface overflow-hidden rounded-xl border">
        <table className="w-full">
          {/* Desktop table header - hidden on mobile */}
          <thead className="bg-surface-hover hidden md:table-header-group">
            <tr>
              <th className="text-foreground px-4 py-3 text-left text-sm font-semibold">Rank</th>
              <th className="text-foreground px-4 py-3 text-left text-sm font-semibold">Photo</th>
              <th className="text-foreground px-4 py-3 text-center text-sm font-semibold">1st</th>
              <th className="text-foreground px-4 py-3 text-center text-sm font-semibold">2nd</th>
              <th className="text-foreground px-4 py-3 text-center text-sm font-semibold">3rd</th>
              <th className="text-foreground px-4 py-3 text-center text-sm font-semibold">Points</th>
              <th className="text-foreground px-4 py-3 text-center text-sm font-semibold">Voters</th>
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {filteredResults.map((result, index) => {
              const isExpanded = expandedId === result.submissionId
              const { firstVoters, secondVoters, thirdVoters } = getVotersForSubmission(result.submissionId)
              const totalVoters = firstVoters.length + secondVoters.length + thirdVoters.length

              return (
                <Fragment key={result.submissionId}>
                  {/* Mobile layout */}
                  <tr className={`md:hidden ${index < 3 ? 'bg-secondary/5' : ''}`}>
                    <td colSpan={7} className="p-4">
                      <div className="flex gap-4">
                        {/* Photo with rank badge overlay */}
                        <div className="relative shrink-0">
                          {result.submission?.photoUrl && (
                            <button
                              type="button"
                              className="block cursor-pointer"
                              onClick={() => result.submission && onPhotoClick(result.submission)}
                            >
                              <img
                                src={result.submission.photoUrl}
                                alt={result.submission.photoTitle}
                                className="h-20 w-20 rounded-lg object-cover"
                              />
                            </button>
                          )}
                          {/* Rank badge - top right corner */}
                          <div className="absolute -top-2 -right-2">
                            {index < 3 ? (
                              <span className="bg-navy inline-flex h-8 w-8 items-center justify-center rounded-full text-lg shadow-md">
                                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                              </span>
                            ) : (
                              <span className="bg-navy text-foreground inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold shadow-sm">
                                {index + 1}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Right side - 2 rows */}
                        <div className="min-w-0 flex-1">
                          {/* Row 1: Photo title / Student name / Class */}
                          <div>
                            <p className="text-foreground truncate text-base leading-tight font-medium">
                              {result.submission?.photoTitle || 'Unknown'}
                            </p>
                            <p className="text-foreground-muted truncate text-base">
                              {result.submission?.name || 'Unknown'} · {formatGrade(result.submission?.grade || '')}
                            </p>
                          </div>
                          {/* Row 2: Vote breakdown */}
                          <div className="mt-2 flex items-center gap-3 text-sm">
                            <div className="flex items-center gap-1">
                              <span className="text-foreground-muted">Points:</span>
                              <span className="text-foreground font-bold">{result.totalPoints}</span>
                            </div>
                            <span className="text-foreground-subtle">|</span>
                            <div className="flex items-center gap-2">
                              <span className="text-foreground-muted">1st:</span>
                              <span className="text-foreground font-semibold">{result.firstChoiceCount}</span>
                              <span className="text-foreground-muted">2nd:</span>
                              <span className="text-foreground font-semibold">{result.secondChoiceCount}</span>
                              <span className="text-foreground-muted">3rd:</span>
                              <span className="text-foreground font-semibold">{result.thirdChoiceCount}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  {/* Desktop layout */}
                  <tr className={`hidden md:table-row ${index < 3 ? 'bg-secondary/5' : ''}`}>
                    <td className="px-4 py-4">
                      {index < 3 ? (
                        <span className="text-2xl">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                      ) : (
                        <span className="bg-surface-hover text-foreground-muted inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold">
                          {index + 1}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {result.submission?.photoUrl && (
                          <button
                            type="button"
                            className="block shrink-0 cursor-pointer"
                            onClick={() => result.submission && onPhotoClick(result.submission)}
                          >
                            <img
                              src={result.submission.photoUrl}
                              alt={result.submission.photoTitle}
                              className="h-12 w-12 rounded-lg object-cover transition-opacity hover:opacity-80"
                            />
                          </button>
                        )}
                        <div>
                          <p className="text-foreground font-medium">{result.submission?.photoTitle || 'Unknown'}</p>
                          <p className="text-foreground-muted text-sm">
                            {result.submission?.name || 'Unknown'} · {formatGrade(result.submission?.grade || '')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-foreground-muted px-4 py-4 text-center">{result.firstChoiceCount}</td>
                    <td className="text-foreground-muted px-4 py-4 text-center">{result.secondChoiceCount}</td>
                    <td className="text-foreground-muted px-4 py-4 text-center">{result.thirdChoiceCount}</td>
                    <td className="text-foreground px-4 py-4 text-center font-bold">{result.totalPoints}</td>
                    <td className="px-4 py-4 text-center">
                      {totalVoters > 0 && (
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : result.submissionId)}
                          className="bg-surface-hover text-foreground-muted hover:bg-accent/20 hover:text-accent inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors"
                        >
                          <Users className="h-3 w-3" />
                          {totalVoters}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-surface-hover/50">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          {firstVoters.length > 0 && (
                            <div>
                              <p className="text-foreground mb-1 font-semibold">1st Place ({firstVoters.length})</p>
                              <div className="max-h-32 space-y-0.5 overflow-y-auto">
                                {firstVoters.map((email) => (
                                  <p key={email} className="text-foreground-muted">
                                    {email}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                          {secondVoters.length > 0 && (
                            <div>
                              <p className="text-foreground mb-1 font-semibold">2nd Place ({secondVoters.length})</p>
                              <div className="max-h-32 space-y-0.5 overflow-y-auto">
                                {secondVoters.map((email) => (
                                  <p key={email} className="text-foreground-muted">
                                    {email}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                          {thirdVoters.length > 0 && (
                            <div>
                              <p className="text-foreground mb-1 font-semibold">3rd Place ({thirdVoters.length})</p>
                              <div className="max-h-32 space-y-0.5 overflow-y-auto">
                                {thirdVoters.map((email) => (
                                  <p key={email} className="text-foreground-muted">
                                    {email}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>

        {/* Scoring Legend */}
        <div className="bg-surface-hover border-border text-foreground-muted border-t px-4 py-3 text-sm">
          Scoring: 1st choice = 3 points, 2nd = 2 points, 3rd = 1 point
        </div>
      </div>
    </div>
  )
}

// Settings View Component
function SettingsView() {
  return (
    <div className="max-w-2xl">
      <h2 className="text-foreground mb-6 text-xl font-bold">Settings</h2>

      <div className="border-border bg-surface overflow-hidden rounded-xl border">
        <div className="p-8 text-center">
          <Settings className="text-foreground-muted mx-auto mb-4 h-12 w-12" />
          <p className="text-foreground-muted">No configurable settings at this time.</p>
        </div>
      </div>
    </div>
  )
}
